import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { systemClock } from '@reos/config';
import { EmptyState, FieldRow, FormSection, SaveStatus, type SaveStatusKind } from '@redhand/ui';
import { DomainApiError } from '../lib/api-error.js';
import {
  calculateSettlements,
  changePrescription,
  closeSettlementRun,
  createPrescription,
  createSettlementRun,
  deliverSettlement,
  endPrescription,
  listCharges,
  listDebtors,
  listPrescriptions,
  listRentPayments,
  listServiceCatalog,
  listSettlementCosts,
  listSettlementRuns,
  listSettlements,
  previewBilling,
  recordRentPayment,
  runBilling,
  sendRentReminder,
  setSettlementCosts,
  settleSettlement,
  syncRentPayments,
  type AllocationMethod,
  type BillingPreviewDto,
  type ChargeDto,
  type ChargeStatus,
  type DebtorDto,
  type PrescriptionDto,
  type PrescriptionStatus,
  type RentPaymentDto,
  type ServiceCatalogEntryDto,
  type SettlementCostDto,
  type SettlementDto,
  type SettlementRunDto,
} from '../lib/rent-api.js';
import { DateField, MonthField } from './date-field.js';
import { CzechNumberInput } from './czech-number-input.js';

/**
 * Sprava najmu (krok FAK3) - zalozky stranky `/sprava-bytu`: predpisy
 * najemneho, hromadne mesicni vystaveni, uhrady a dluznici, rocni
 * vyuctovani sluzeb (z. c. 67/2013 Sb.).
 *
 * Vsechny texty jdou pres `t()` (CLAUDE.md pravidlo 5); castky formatuje
 * `money()` stejne jako v `invoices.tsx` (krok FAK2).
 */

const LOCALE = 'cs' as const;

const PRESCRIPTION_STATUS_KEY: Readonly<Record<PrescriptionStatus, MessageKey>> = {
  active: 'webAdmin.spravaBytu.prescriptions.statusActive',
  superseded: 'webAdmin.spravaBytu.prescriptions.statusSuperseded',
  ended: 'webAdmin.spravaBytu.prescriptions.statusEnded',
};

const CHARGE_STATUS_KEY: Readonly<Record<ChargeStatus, MessageKey>> = {
  planned: 'webAdmin.spravaBytu.billing.statusPlanned',
  issued: 'webAdmin.spravaBytu.billing.statusIssued',
  partially_paid: 'webAdmin.spravaBytu.billing.statusPartiallyPaid',
  paid: 'webAdmin.spravaBytu.billing.statusPaid',
  cancelled: 'webAdmin.spravaBytu.billing.statusCancelled',
};

const METHOD_KEY: Readonly<Record<AllocationMethod, MessageKey>> = {
  persons: 'webAdmin.spravaBytu.settlement.methodPersons',
  area: 'webAdmin.spravaBytu.settlement.methodArea',
  equal: 'webAdmin.spravaBytu.settlement.methodEqual',
  direct: 'webAdmin.spravaBytu.settlement.methodDirect',
};

const RUN_STATUS_KEY: Readonly<Record<SettlementRunDto['status'], MessageKey>> = {
  draft: 'webAdmin.spravaBytu.settlement.statusDraft',
  calculated: 'webAdmin.spravaBytu.settlement.statusCalculated',
  closed: 'webAdmin.spravaBytu.settlement.statusClosed',
};

const SETTLEMENT_STATUS_KEY: Readonly<Record<SettlementDto['status'], MessageKey>> = {
  calculated: 'webAdmin.spravaBytu.settlement.settlementStatusCalculated',
  delivered: 'webAdmin.spravaBytu.settlement.settlementStatusDelivered',
  settled: 'webAdmin.spravaBytu.settlement.settlementStatusSettled',
};

function money(amount: string): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return amount;
  return new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function today(): string {
  return systemClock.now().toISOString().slice(0, 10);
}

function currentPeriod(): string {
  return `${today().slice(0, 7)}-01`;
}

function errorDetailProp(value: string | undefined): { errorDetail?: string } {
  return value === undefined ? {} : { errorDetail: value };
}

function domainErrorMessage(error: unknown): string | undefined {
  return error instanceof DomainApiError ? error.message : undefined;
}

// --------------------------------------------------------- bod 1: predpisy

interface PrescriptionFormState {
  unitLabel: string;
  name: string;
  street: string;
  city: string;
  zip: string;
  email: string;
  rentAmount: string;
  dueDay: string;
  variableSymbol: string;
  bankAccountIban: string;
  personsCount: string;
  floorAreaM2: string;
  validFrom: string;
  note: string;
  services: Record<string, string>;
}

function emptyForm(): PrescriptionFormState {
  return {
    unitLabel: '',
    name: '',
    street: '',
    city: '',
    zip: '',
    email: '',
    rentAmount: '',
    dueDay: '15',
    variableSymbol: '',
    bankAccountIban: '',
    personsCount: '',
    floorAreaM2: '',
    validFrom: today(),
    note: '',
    services: {},
  };
}

function formFrom(prescription: PrescriptionDto): PrescriptionFormState {
  return {
    unitLabel: prescription.unitLabel,
    name: prescription.customer.name,
    street: prescription.customer.street,
    city: prescription.customer.city,
    zip: prescription.customer.zip,
    email: prescription.customer.email ?? '',
    rentAmount: prescription.rentAmount,
    dueDay: String(prescription.dueDay),
    variableSymbol: prescription.variableSymbol,
    bankAccountIban: prescription.bankAccountIban ?? '',
    personsCount: prescription.personsCount === null ? '' : String(prescription.personsCount),
    floorAreaM2: prescription.floorAreaM2 ?? '',
    validFrom: today(),
    note: prescription.note ?? '',
    services: Object.fromEntries(
      prescription.services.map((service) => [service.serviceKey, service.advanceAmount]),
    ),
  };
}

function PrescriptionForm({
  catalog,
  changing,
  onSaved,
  onCancel,
}: {
  readonly catalog: readonly ServiceCatalogEntryDto[];
  readonly changing: PrescriptionDto | null;
  readonly onSaved: () => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [form, setForm] = React.useState<PrescriptionFormState>(() =>
    changing === null ? emptyForm() : formFrom(changing),
  );
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');
  const [errorDetail, setErrorDetail] = React.useState<string | undefined>(undefined);

  function set<K extends keyof PrescriptionFormState>(
    key: K,
    value: PrescriptionFormState[K],
  ): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function servicesPayload(): Array<{ serviceKey: string; advanceAmount: string }> {
    return Object.entries(form.services)
      .filter(([, amount]) => amount.trim() !== '' && Number(amount) > 0)
      .map(([serviceKey, advanceAmount]) => ({ serviceKey, advanceAmount: advanceAmount.trim() }));
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setStatus('saving');
    setErrorDetail(undefined);
    const core = {
      unitLabel: form.unitLabel.trim(),
      customer: {
        name: form.name.trim(),
        ico: null,
        dic: null,
        street: form.street.trim(),
        city: form.city.trim(),
        zip: form.zip.trim(),
        email: form.email.trim() === '' ? null : form.email.trim(),
      },
      rentAmount: form.rentAmount.trim(),
      dueDay: Number(form.dueDay),
      validFrom: form.validFrom,
      bankAccountIban: form.bankAccountIban.trim() === '' ? null : form.bankAccountIban.trim(),
      personsCount: form.personsCount.trim() === '' ? null : Number(form.personsCount),
      floorAreaM2: form.floorAreaM2.trim() === '' ? null : form.floorAreaM2.trim(),
      note: form.note.trim() === '' ? null : form.note.trim(),
      services: servicesPayload(),
    };
    try {
      if (changing === null) {
        await createPrescription({
          ...core,
          ...(form.variableSymbol.trim() === ''
            ? {}
            : { variableSymbol: form.variableSymbol.trim() }),
        });
      } else {
        await changePrescription(changing.id, core);
      }
      setStatus('saved');
      onSaved();
    } catch (error) {
      setStatus('error');
      setErrorDetail(domainErrorMessage(error));
    }
  }

  // Do predpisu patri jen sluzby, ktere se uctuji v zalohach (z. 67/2013).
  const advanceServices = catalog.filter((entry) => entry.defaultState === 'v-zalohach');

  return (
    <form onSubmit={(e) => void submit(e)} data-testid="rent-prescription-form">
      <FormSection
        title={t(
          changing === null
            ? 'webAdmin.spravaBytu.prescriptions.formHeading'
            : 'webAdmin.spravaBytu.prescriptions.changeHeading',
          { locale: LOCALE },
        )}
        footer={
          <>
            <button type="submit" className="rh-btn" data-testid="rent-prescription-submit">
              {t(
                changing === null
                  ? 'webAdmin.spravaBytu.prescriptions.submitCreate'
                  : 'webAdmin.spravaBytu.prescriptions.submitChange',
                { locale: LOCALE },
              )}
            </button>
            <button
              type="button"
              className="rh-btn"
              onClick={onCancel}
              data-testid="rent-prescription-cancel"
            >
              {t('webAdmin.spravaBytu.prescriptions.cancel', { locale: LOCALE })}
            </button>
            <SaveStatus
              status={status}
              texts={{
                saving: '…',
                saved: t('webAdmin.spravaBytu.prescriptions.saved', { locale: LOCALE }),
                error: '',
              }}
              {...errorDetailProp(errorDetail)}
            />
          </>
        }
      >
        <FieldRow
          label={t('webAdmin.spravaBytu.prescriptions.unitLabel', { locale: LOCALE })}
          htmlFor="rent-unit"
        >
          <input
            id="rent-unit"
            required
            className="rh-input block"
            value={form.unitLabel}
            onChange={(e) => set('unitLabel', e.target.value)}
            data-testid="rent-form-unit"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.prescriptions.tenantName', { locale: LOCALE })}
          htmlFor="rent-name"
        >
          <input
            id="rent-name"
            required
            className="rh-input block"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            data-testid="rent-form-name"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.prescriptions.street', { locale: LOCALE })}
          htmlFor="rent-street"
        >
          <input
            id="rent-street"
            required
            className="rh-input block"
            value={form.street}
            onChange={(e) => set('street', e.target.value)}
            data-testid="rent-form-street"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.prescriptions.city', { locale: LOCALE })}
          htmlFor="rent-city"
        >
          <input
            id="rent-city"
            required
            className="rh-input block"
            value={form.city}
            onChange={(e) => set('city', e.target.value)}
            data-testid="rent-form-city"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.prescriptions.zip', { locale: LOCALE })}
          htmlFor="rent-zip"
        >
          <input
            id="rent-zip"
            required
            className="rh-input block w-32"
            value={form.zip}
            onChange={(e) => set('zip', e.target.value)}
            data-testid="rent-form-zip"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.prescriptions.email', { locale: LOCALE })}
          htmlFor="rent-email"
        >
          <input
            id="rent-email"
            type="email"
            className="rh-input block"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            data-testid="rent-form-email"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.prescriptions.rentAmount', { locale: LOCALE })}
          htmlFor="rent-amount"
        >
          {/*
            Krok UXD2: pole penez a ploch jde pres `CzechNumberInput`.
            `inputMode="decimal"` sice klavesnici otevrel spravne, ale hodnota
            z pole letela do API SUROVA (`form.rentAmount.trim()`) — presne
            vada EDIT1 B1, jen o jedno pole dal.
          */}
          <CzechNumberInput
            id="rent-amount"
            required
            className="rh-input block w-40"
            value={form.rentAmount}
            onValueChange={(next) => set('rentAmount', next)}
            testId="rent-form-amount"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.prescriptions.dueDay', { locale: LOCALE })}
          htmlFor="rent-due-day"
        >
          <input
            id="rent-due-day"
            required
            type="number"
            min={1}
            max={31}
            className="rh-input block w-24"
            value={form.dueDay}
            onChange={(e) => set('dueDay', e.target.value)}
            data-testid="rent-form-due-day"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.prescriptions.validFrom', { locale: LOCALE })}
          htmlFor="rent-valid-from"
        >
          <DateField
            id="rent-valid-from"
            required
            className="rh-input block w-48"
            value={form.validFrom}
            onValueChange={(next) => set('validFrom', next)}
            testId="rent-form-valid-from"
          />
        </FieldRow>
        {changing === null && (
          <FieldRow
            label={t('webAdmin.spravaBytu.prescriptions.variableSymbol', { locale: LOCALE })}
            htmlFor="rent-vs"
          >
            <input
              id="rent-vs"
              className="rh-input block w-40"
              value={form.variableSymbol}
              onChange={(e) => set('variableSymbol', e.target.value)}
              data-testid="rent-form-vs"
            />
          </FieldRow>
        )}
        <FieldRow
          label={t('webAdmin.spravaBytu.prescriptions.bankAccount', { locale: LOCALE })}
          htmlFor="rent-bank"
        >
          <input
            id="rent-bank"
            className="rh-input block"
            value={form.bankAccountIban}
            onChange={(e) => set('bankAccountIban', e.target.value)}
            data-testid="rent-form-bank"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.prescriptions.personsCount', { locale: LOCALE })}
          htmlFor="rent-persons"
        >
          <input
            id="rent-persons"
            type="number"
            min={0}
            className="rh-input block w-24"
            value={form.personsCount}
            onChange={(e) => set('personsCount', e.target.value)}
            data-testid="rent-form-persons"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.prescriptions.floorArea', { locale: LOCALE })}
          htmlFor="rent-area"
        >
          <CzechNumberInput
            id="rent-area"
            className="rh-input block w-32"
            value={form.floorAreaM2}
            onValueChange={(next) => set('floorAreaM2', next)}
            testId="rent-form-area"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.prescriptions.note', { locale: LOCALE })}
          htmlFor="rent-note"
        >
          <input
            id="rent-note"
            className="rh-input block"
            value={form.note}
            onChange={(e) => set('note', e.target.value)}
            data-testid="rent-form-note"
          />
        </FieldRow>
      </FormSection>

      <FormSection
        title={t('webAdmin.spravaBytu.prescriptions.servicesHeading', { locale: LOCALE })}
      >
        <p className="text-sm text-gray-600">
          {t('webAdmin.spravaBytu.prescriptions.servicesHint', { locale: LOCALE })}
        </p>
        {advanceServices.map((entry) => (
          <FieldRow key={entry.key} label={entry.label} htmlFor={`rent-service-${entry.key}`}>
            <CzechNumberInput
              id={`rent-service-${entry.key}`}
              className="rh-input block w-40"
              value={form.services[entry.key] ?? ''}
              onValueChange={(next) => set('services', { ...form.services, [entry.key]: next })}
              testId={`rent-form-service-${entry.key}`}
            />
          </FieldRow>
        ))}
      </FormSection>
    </form>
  );
}

export function PrescriptionsTab(): React.JSX.Element {
  const [items, setItems] = React.useState<readonly PrescriptionDto[] | null>(null);
  const [catalog, setCatalog] = React.useState<readonly ServiceCatalogEntryDto[]>([]);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<
    { readonly kind: 'list' } | { readonly kind: 'form'; readonly changing: PrescriptionDto | null }
  >({ kind: 'list' });
  const [ending, setEnding] = React.useState<{
    readonly id: string;
    readonly validTo: string;
  } | null>(null);

  const reload = React.useCallback(async () => {
    setLoadError(null);
    try {
      const [list, entries] = await Promise.all([listPrescriptions(), listServiceCatalog()]);
      setItems(list);
      setCatalog(entries);
    } catch (error) {
      setLoadError(
        domainErrorMessage(error) ?? t('webAdmin.spravaBytu.loadError', { locale: LOCALE }),
      );
    }
  }, []);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  if (mode.kind === 'form') {
    return (
      <PrescriptionForm
        catalog={catalog}
        changing={mode.changing}
        onSaved={() => {
          setMode({ kind: 'list' });
          void reload();
        }}
        onCancel={() => setMode({ kind: 'list' })}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="rent-prescriptions-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          {t('webAdmin.spravaBytu.prescriptions.heading', { locale: LOCALE })}
        </h2>
        <button
          type="button"
          className="rh-btn"
          onClick={() => setMode({ kind: 'form', changing: null })}
          data-testid="rent-new-prescription"
        >
          {t('webAdmin.spravaBytu.prescriptions.newButton', { locale: LOCALE })}
        </button>
      </div>

      {loadError !== null && (
        <p role="alert" className="text-red-600">
          {loadError}
        </p>
      )}
      {items === null && loadError === null && (
        <p role="status">{t('webAdmin.spravaBytu.loading', { locale: LOCALE })}</p>
      )}
      {items !== null && items.length === 0 && (
        <EmptyState title={t('webAdmin.spravaBytu.prescriptions.empty', { locale: LOCALE })} />
      )}

      {items !== null && items.length > 0 && (
        <table className="rh-table" data-testid="rent-prescriptions-table">
          <thead>
            <tr>
              <th>{t('webAdmin.spravaBytu.prescriptions.colUnit', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.prescriptions.colTenant', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.prescriptions.colVs', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.prescriptions.colRent', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.prescriptions.colServices', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.prescriptions.colTotal', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.prescriptions.colDueDay', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.prescriptions.colValidity', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.prescriptions.colStatus', { locale: LOCALE })}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((prescription) => {
              const advances = prescription.services.reduce(
                (sum, service) => sum + Number(service.advanceAmount),
                0,
              );
              return (
                <tr key={prescription.id} data-testid={`rent-prescription-row-${prescription.id}`}>
                  <td>{prescription.unitLabel}</td>
                  <td>{prescription.customer.name}</td>
                  <td className="font-mono">{prescription.variableSymbol}</td>
                  <td>{money(prescription.rentAmount)}</td>
                  <td>{money(String(advances))}</td>
                  <td>{money(prescription.monthlyTotal)}</td>
                  <td>
                    {t('webAdmin.spravaBytu.prescriptions.dueDayValue', {
                      locale: LOCALE,
                      values: { day: prescription.dueDay },
                    })}
                  </td>
                  <td>
                    {prescription.validFrom}
                    {prescription.validTo !== null ? ` – ${prescription.validTo}` : ''}
                  </td>
                  <td>{t(PRESCRIPTION_STATUS_KEY[prescription.status], { locale: LOCALE })}</td>
                  <td>
                    {prescription.status === 'active' && (
                      <>
                        <button
                          type="button"
                          className="rh-btn"
                          onClick={() => setMode({ kind: 'form', changing: prescription })}
                          data-testid={`rent-change-${prescription.id}`}
                        >
                          {t('webAdmin.spravaBytu.prescriptions.changeButton', { locale: LOCALE })}
                        </button>
                        <button
                          type="button"
                          className="rh-btn"
                          onClick={() => setEnding({ id: prescription.id, validTo: today() })}
                          data-testid={`rent-end-${prescription.id}`}
                        >
                          {t('webAdmin.spravaBytu.prescriptions.endButton', { locale: LOCALE })}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {ending !== null && (
        <FormSection title={t('webAdmin.spravaBytu.prescriptions.endPrompt', { locale: LOCALE })}>
          <DateField
            className="rh-input w-48"
            value={ending.validTo}
            onValueChange={(next) => setEnding({ ...ending, validTo: next })}
            ariaLabel={t('webAdmin.spravaBytu.prescriptions.endPrompt', { locale: LOCALE })}
            testId="rent-end-date"
          />
          <button
            type="button"
            className="rh-btn"
            onClick={() => {
              void endPrescription(ending.id, ending.validTo).then(() => {
                setEnding(null);
                return reload();
              });
            }}
            data-testid="rent-end-confirm"
          >
            {t('webAdmin.spravaBytu.prescriptions.endConfirm', { locale: LOCALE })}
          </button>
        </FormSection>
      )}
    </div>
  );
}

// ------------------------------------------------- bod 2: mesicni vystaveni

export function BillingTab(): React.JSX.Element {
  const [period, setPeriod] = React.useState(currentPeriod());
  const [preview, setPreview] = React.useState<BillingPreviewDto | null>(null);
  const [charges, setCharges] = React.useState<readonly ChargeDto[]>([]);
  const [issueDocuments, setIssueDocuments] = React.useState(true);
  const [sendEmail, setSendEmail] = React.useState(false);
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');
  const [result, setResult] = React.useState<string | null>(null);
  const [errorDetail, setErrorDetail] = React.useState<string | undefined>(undefined);

  const load = React.useCallback(async () => {
    setErrorDetail(undefined);
    try {
      const [previewData, chargeData] = await Promise.all([
        previewBilling(period),
        listCharges({ period }),
      ]);
      setPreview(previewData);
      setCharges(chargeData);
    } catch (error) {
      setErrorDetail(
        domainErrorMessage(error) ?? t('webAdmin.spravaBytu.loadError', { locale: LOCALE }),
      );
    }
  }, [period]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function run(): Promise<void> {
    setStatus('saving');
    setResult(null);
    setErrorDetail(undefined);
    try {
      const outcome = await runBilling({ period, issueDocuments, sendEmail });
      const skipped = outcome.items.filter((item) => item.status === 'skipped').length;
      setResult(
        t('webAdmin.spravaBytu.billing.runDone', {
          locale: LOCALE,
          values: { issued: outcome.run.issuedCount, skipped, failed: outcome.run.failedCount },
        }),
      );
      setStatus('saved');
      await load();
    } catch (error) {
      setStatus('error');
      setErrorDetail(domainErrorMessage(error));
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="rent-billing-tab">
      <FormSection title={t('webAdmin.spravaBytu.billing.heading', { locale: LOCALE })}>
        <FieldRow
          label={t('webAdmin.spravaBytu.billing.periodLabel', { locale: LOCALE })}
          htmlFor="rent-period"
        >
          <MonthField
            id="rent-period"
            className="rh-input w-48"
            value={period.slice(0, 7)}
            onValueChange={(next) => setPeriod(`${next}-01`)}
            testId="rent-billing-period"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.billing.issueDocuments', { locale: LOCALE })}
          htmlFor="rent-issue-docs"
        >
          <input
            id="rent-issue-docs"
            type="checkbox"
            checked={issueDocuments}
            onChange={(e) => setIssueDocuments(e.target.checked)}
            data-testid="rent-billing-issue-docs"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.billing.sendEmail', { locale: LOCALE })}
          htmlFor="rent-send-email"
        >
          <input
            id="rent-send-email"
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            data-testid="rent-billing-send-email"
          />
        </FieldRow>
      </FormSection>

      {preview !== null && (
        <div className="rh-card p-4" data-testid="rent-billing-preview">
          <p className="font-semibold">
            {t('webAdmin.spravaBytu.billing.previewHeading', { locale: LOCALE })}
          </p>
          <p className="text-sm text-gray-600">
            {t('webAdmin.spravaBytu.billing.planned', {
              locale: LOCALE,
              values: { count: preview.plannedCount },
            })}{' '}
            ·{' '}
            {t('webAdmin.spravaBytu.billing.skipped', {
              locale: LOCALE,
              values: { count: preview.skippedCount },
            })}{' '}
            · {t('webAdmin.spravaBytu.billing.total', { locale: LOCALE })}:{' '}
            {money(preview.totalAmount)}{' '}
            {t('webAdmin.spravaBytu.currencySuffix', { locale: LOCALE })}
          </p>
          {preview.rows.length === 0 ? (
            <p className="text-sm text-gray-600">
              {t('webAdmin.spravaBytu.billing.empty', { locale: LOCALE })}
            </p>
          ) : (
            <table className="rh-table mt-2">
              <thead>
                <tr>
                  <th>{t('webAdmin.spravaBytu.prescriptions.colUnit', { locale: LOCALE })}</th>
                  <th>{t('webAdmin.spravaBytu.prescriptions.colTenant', { locale: LOCALE })}</th>
                  <th>{t('webAdmin.spravaBytu.prescriptions.colVs', { locale: LOCALE })}</th>
                  <th>{t('webAdmin.spravaBytu.billing.colDue', { locale: LOCALE })}</th>
                  <th>{t('webAdmin.spravaBytu.prescriptions.colTotal', { locale: LOCALE })}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr
                    key={row.prescriptionId}
                    data-testid={`rent-preview-row-${row.prescriptionId}`}
                  >
                    <td>{row.unitLabel}</td>
                    <td>{row.customerName}</td>
                    <td className="font-mono">{row.variableSymbol}</td>
                    <td>{row.dueDate}</td>
                    <td>{money(row.totalAmount)}</td>
                    <td>
                      {row.alreadyCharged
                        ? t('webAdmin.spravaBytu.billing.alreadyCharged', { locale: LOCALE })
                        : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              className="rh-btn"
              disabled={preview.plannedCount === 0}
              onClick={() => void run()}
              data-testid="rent-billing-run"
            >
              {t('webAdmin.spravaBytu.billing.runButton', { locale: LOCALE })}
            </button>
            <SaveStatus
              status={status}
              texts={{ saving: '…', saved: result ?? '', error: '' }}
              {...errorDetailProp(errorDetail)}
            />
          </div>
        </div>
      )}

      {charges.length > 0 && (
        <div className="rh-card p-4" data-testid="rent-charges-card">
          <p className="font-semibold">
            {t('webAdmin.spravaBytu.billing.chargesHeading', { locale: LOCALE })}
          </p>
          <table className="rh-table mt-2">
            <thead>
              <tr>
                <th>{t('webAdmin.spravaBytu.prescriptions.colVs', { locale: LOCALE })}</th>
                <th>{t('webAdmin.spravaBytu.billing.colDue', { locale: LOCALE })}</th>
                <th>{t('webAdmin.spravaBytu.prescriptions.colTotal', { locale: LOCALE })}</th>
                <th>{t('webAdmin.spravaBytu.billing.colPaid', { locale: LOCALE })}</th>
                <th>{t('webAdmin.spravaBytu.prescriptions.colStatus', { locale: LOCALE })}</th>
              </tr>
            </thead>
            <tbody>
              {charges.map((charge) => (
                <tr key={charge.id} data-testid={`rent-charge-row-${charge.id}`}>
                  <td className="font-mono">{charge.variableSymbol}</td>
                  <td>{charge.dueDate}</td>
                  <td>{money(charge.totalAmount)}</td>
                  <td>{money(charge.paidAmount)}</td>
                  <td>{t(CHARGE_STATUS_KEY[charge.status], { locale: LOCALE })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------ bod 3: uhrady a dluznici

export function DebtorsTab(): React.JSX.Element {
  const [asOf, setAsOf] = React.useState(today());
  const [debtors, setDebtors] = React.useState<readonly DebtorDto[] | null>(null);
  const [payments, setPayments] = React.useState<readonly RentPaymentDto[]>([]);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [errorDetail, setErrorDetail] = React.useState<string | undefined>(undefined);
  const [payFor, setPayFor] = React.useState<{
    readonly prescriptionId: string;
    readonly amount: string;
    readonly paidOn: string;
  } | null>(null);

  const reload = React.useCallback(async () => {
    setErrorDetail(undefined);
    try {
      const [rows, paymentRows] = await Promise.all([listDebtors(asOf), listRentPayments()]);
      setDebtors(rows);
      setPayments(paymentRows.slice(0, 20));
    } catch (error) {
      setErrorDetail(
        domainErrorMessage(error) ?? t('webAdmin.spravaBytu.loadError', { locale: LOCALE }),
      );
    }
  }, [asOf]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  async function sync(): Promise<void> {
    setNotice(null);
    try {
      const outcome = await syncRentPayments({});
      setNotice(
        t('webAdmin.spravaBytu.debtors.syncDone', {
          locale: LOCALE,
          values: { count: outcome.importedPayments, checked: outcome.checkedCharges },
        }),
      );
      await reload();
    } catch (error) {
      setErrorDetail(domainErrorMessage(error));
    }
  }

  async function remind(prescriptionId: string): Promise<void> {
    setNotice(null);
    try {
      await sendRentReminder(prescriptionId, {});
      setNotice(t('webAdmin.spravaBytu.debtors.reminderSent', { locale: LOCALE }));
      await reload();
    } catch (error) {
      setErrorDetail(domainErrorMessage(error));
    }
  }

  async function pay(): Promise<void> {
    if (payFor === null) return;
    setNotice(null);
    try {
      await recordRentPayment({
        prescriptionId: payFor.prescriptionId,
        paidOn: payFor.paidOn,
        amount: payFor.amount,
        method: 'bank',
      });
      setNotice(t('webAdmin.spravaBytu.debtors.paymentSaved', { locale: LOCALE }));
      setPayFor(null);
      await reload();
    } catch (error) {
      setErrorDetail(domainErrorMessage(error));
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="rent-debtors-tab">
      <div className="flex items-end gap-2">
        <FieldRow
          label={t('webAdmin.spravaBytu.debtors.asOf', { locale: LOCALE })}
          htmlFor="rent-as-of"
        >
          <DateField
            id="rent-as-of"
            className="rh-input w-48"
            value={asOf}
            onValueChange={setAsOf}
            testId="rent-debtors-as-of"
          />
        </FieldRow>
        <button
          type="button"
          className="rh-btn"
          onClick={() => void sync()}
          data-testid="rent-debtors-sync"
        >
          {t('webAdmin.spravaBytu.debtors.syncButton', { locale: LOCALE })}
        </button>
      </div>

      {notice !== null && (
        <p role="status" className="text-sm text-green-700">
          {notice}
        </p>
      )}
      {errorDetail !== undefined && (
        <p role="alert" className="text-red-600">
          {errorDetail}
        </p>
      )}
      {debtors === null && (
        <p role="status">{t('webAdmin.spravaBytu.loading', { locale: LOCALE })}</p>
      )}
      {debtors !== null && debtors.length === 0 && (
        <EmptyState title={t('webAdmin.spravaBytu.debtors.empty', { locale: LOCALE })} />
      )}

      {debtors !== null && debtors.length > 0 && (
        <table className="rh-table" data-testid="rent-debtors-table">
          <thead>
            <tr>
              <th>{t('webAdmin.spravaBytu.debtors.colTenant', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.debtors.colUnit', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.debtors.colVs', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.debtors.colBalance', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.debtors.colDaysOverdue', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.debtors.colOldest', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.debtors.colReminder', { locale: LOCALE })}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {debtors.map((debtor) => (
              <tr
                key={debtor.prescriptionId}
                data-testid={`rent-debtor-row-${debtor.prescriptionId}`}
              >
                <td>{debtor.customerName}</td>
                <td>{debtor.unitLabel}</td>
                <td className="font-mono">{debtor.variableSymbol}</td>
                <td>{money(debtor.balance)}</td>
                <td>{debtor.daysOverdue}</td>
                <td>{debtor.oldestOverdueDueDate ?? '—'}</td>
                <td>
                  {debtor.lastReminderLevel === null
                    ? t('webAdmin.spravaBytu.debtors.reminderNone', { locale: LOCALE })
                    : t('webAdmin.spravaBytu.debtors.reminderLevel', {
                        locale: LOCALE,
                        values: { level: debtor.lastReminderLevel },
                      })}
                </td>
                <td>
                  <button
                    type="button"
                    className="rh-btn"
                    onClick={() => void remind(debtor.prescriptionId)}
                    data-testid={`rent-remind-${debtor.prescriptionId}`}
                  >
                    {t('webAdmin.spravaBytu.debtors.reminderButton', { locale: LOCALE })}
                  </button>
                  <button
                    type="button"
                    className="rh-btn"
                    onClick={() =>
                      setPayFor({
                        prescriptionId: debtor.prescriptionId,
                        amount: debtor.balance,
                        paidOn: today(),
                      })
                    }
                    data-testid={`rent-pay-${debtor.prescriptionId}`}
                  >
                    {t('webAdmin.spravaBytu.debtors.paymentSubmit', { locale: LOCALE })}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {payFor !== null && (
        <FormSection title={t('webAdmin.spravaBytu.debtors.paymentHeading', { locale: LOCALE })}>
          <FieldRow
            label={t('webAdmin.spravaBytu.debtors.paymentDate', { locale: LOCALE })}
            htmlFor="rent-pay-date"
          >
            <DateField
              id="rent-pay-date"
              className="rh-input w-48"
              value={payFor.paidOn}
              onValueChange={(next) => setPayFor({ ...payFor, paidOn: next })}
              testId="rent-pay-date"
            />
          </FieldRow>
          <FieldRow
            label={t('webAdmin.spravaBytu.debtors.paymentAmount', { locale: LOCALE })}
            htmlFor="rent-pay-amount"
          >
            <CzechNumberInput
              id="rent-pay-amount"
              className="rh-input w-40"
              value={payFor.amount}
              onValueChange={(next) => setPayFor({ ...payFor, amount: next })}
              testId="rent-pay-amount"
            />
          </FieldRow>
          <button
            type="button"
            className="rh-btn"
            onClick={() => void pay()}
            data-testid="rent-pay-submit"
          >
            {t('webAdmin.spravaBytu.debtors.paymentSubmit', { locale: LOCALE })}
          </button>
        </FormSection>
      )}

      {payments.length > 0 && (
        <div className="rh-card p-4" data-testid="rent-payments-card">
          <p className="font-semibold">
            {t('webAdmin.spravaBytu.debtors.paymentsHeading', { locale: LOCALE })}
          </p>
          <table className="rh-table mt-2">
            <thead>
              <tr>
                <th>{t('webAdmin.spravaBytu.debtors.paymentDate', { locale: LOCALE })}</th>
                <th>{t('webAdmin.spravaBytu.debtors.paymentAmount', { locale: LOCALE })}</th>
                <th>{t('webAdmin.spravaBytu.debtors.colVs', { locale: LOCALE })}</th>
                <th>{t('webAdmin.spravaBytu.debtors.paymentPrescription', { locale: LOCALE })}</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td>{payment.paidOn}</td>
                  <td>{money(payment.amount)}</td>
                  <td className="font-mono">{payment.variableSymbol ?? '—'}</td>
                  <td>
                    {t(
                      payment.source === 'manual'
                        ? 'webAdmin.spravaBytu.debtors.sourceManual'
                        : 'webAdmin.spravaBytu.debtors.sourceMyucto',
                      { locale: LOCALE },
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -------------------------------------------- bod 4: vyuctovani sluzeb

export function SettlementTab(): React.JSX.Element {
  const [runs, setRuns] = React.useState<readonly SettlementRunDto[] | null>(null);
  const [selected, setSelected] = React.useState<SettlementRunDto | null>(null);
  const [costs, setCosts] = React.useState<readonly SettlementCostDto[]>([]);
  const [settlements, setSettlements] = React.useState<readonly SettlementDto[]>([]);
  const [catalog, setCatalog] = React.useState<readonly ServiceCatalogEntryDto[]>([]);
  const [draftCosts, setDraftCosts] = React.useState<
    Record<string, { amount: string; method: AllocationMethod }>
  >({});
  const [newRun, setNewRun] = React.useState({
    label: '',
    year: String(new Date(today()).getFullYear() - 1),
  });
  const [notice, setNotice] = React.useState<string | null>(null);
  const [errorDetail, setErrorDetail] = React.useState<string | undefined>(undefined);

  const reloadRuns = React.useCallback(async () => {
    try {
      const [runRows, entries] = await Promise.all([listSettlementRuns(), listServiceCatalog()]);
      setRuns(runRows);
      setCatalog(entries);
    } catch (error) {
      setErrorDetail(
        domainErrorMessage(error) ?? t('webAdmin.spravaBytu.loadError', { locale: LOCALE }),
      );
    }
  }, []);

  const openRun = React.useCallback(async (run: SettlementRunDto) => {
    setSelected(run);
    setNotice(null);
    try {
      const [costRows, settlementRows] = await Promise.all([
        listSettlementCosts(run.id),
        listSettlements({ runId: run.id }),
      ]);
      setCosts(costRows);
      setSettlements(settlementRows);
      setDraftCosts(
        Object.fromEntries(
          costRows.map((cost) => [
            cost.serviceKey,
            { amount: cost.totalCost, method: cost.method },
          ]),
        ),
      );
    } catch (error) {
      setErrorDetail(domainErrorMessage(error));
    }
  }, []);

  React.useEffect(() => {
    void reloadRuns();
  }, [reloadRuns]);

  async function create(): Promise<void> {
    setErrorDetail(undefined);
    try {
      const run = await createSettlementRun({
        label: newRun.label.trim(),
        year: Number(newRun.year),
      });
      setNewRun({ label: '', year: newRun.year });
      await reloadRuns();
      await openRun(run);
    } catch (error) {
      setErrorDetail(domainErrorMessage(error));
    }
  }

  async function saveCosts(): Promise<void> {
    if (selected === null) return;
    setErrorDetail(undefined);
    try {
      const payload = Object.entries(draftCosts)
        .filter(([, value]) => value.amount.trim() !== '' && Number(value.amount) > 0)
        .map(([serviceKey, value]) => ({
          serviceKey,
          method: value.method,
          totalCost: value.amount.trim(),
        }));
      await setSettlementCosts(selected.id, { costs: payload });
      setNotice(t('webAdmin.spravaBytu.settlement.costsSaved', { locale: LOCALE }));
      await openRun(selected);
    } catch (error) {
      setErrorDetail(domainErrorMessage(error));
    }
  }

  async function calculate(): Promise<void> {
    if (selected === null) return;
    setErrorDetail(undefined);
    try {
      setSettlements(await calculateSettlements(selected.id));
      await reloadRuns();
    } catch (error) {
      setErrorDetail(domainErrorMessage(error));
    }
  }

  async function act(action: 'deliver' | 'settle', id: string): Promise<void> {
    setErrorDetail(undefined);
    try {
      if (action === 'deliver') await deliverSettlement(id);
      else await settleSettlement(id);
      if (selected !== null) await openRun(selected);
    } catch (error) {
      setErrorDetail(domainErrorMessage(error));
    }
  }

  const advanceServices = catalog.filter((entry) => entry.defaultState === 'v-zalohach');

  return (
    <div className="flex flex-col gap-4" data-testid="rent-settlement-tab">
      <p className="text-sm text-gray-600">
        {t('webAdmin.spravaBytu.settlement.legalNote', { locale: LOCALE })}
      </p>

      <FormSection title={t('webAdmin.spravaBytu.settlement.newRunHeading', { locale: LOCALE })}>
        <FieldRow
          label={t('webAdmin.spravaBytu.settlement.label', { locale: LOCALE })}
          htmlFor="settlement-label"
        >
          <input
            id="settlement-label"
            className="rh-input block"
            value={newRun.label}
            onChange={(e) => setNewRun({ ...newRun, label: e.target.value })}
            data-testid="settlement-label"
          />
        </FieldRow>
        <FieldRow
          label={t('webAdmin.spravaBytu.settlement.year', { locale: LOCALE })}
          htmlFor="settlement-year"
        >
          <input
            id="settlement-year"
            type="number"
            className="rh-input block w-32"
            value={newRun.year}
            onChange={(e) => setNewRun({ ...newRun, year: e.target.value })}
            data-testid="settlement-year"
          />
        </FieldRow>
        <button
          type="button"
          className="rh-btn"
          onClick={() => void create()}
          data-testid="settlement-create"
        >
          {t('webAdmin.spravaBytu.settlement.createRun', { locale: LOCALE })}
        </button>
      </FormSection>

      {errorDetail !== undefined && (
        <p role="alert" className="text-red-600">
          {errorDetail}
        </p>
      )}
      {notice !== null && (
        <p role="status" className="text-sm text-green-700">
          {notice}
        </p>
      )}

      {runs !== null && runs.length === 0 && (
        <EmptyState title={t('webAdmin.spravaBytu.settlement.empty', { locale: LOCALE })} />
      )}
      {runs !== null && runs.length > 0 && (
        <table className="rh-table" data-testid="settlement-runs-table">
          <thead>
            <tr>
              <th>{t('webAdmin.spravaBytu.settlement.label', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.settlement.year', { locale: LOCALE })}</th>
              <th>{t('webAdmin.spravaBytu.prescriptions.colStatus', { locale: LOCALE })}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr key={run.id} data-testid={`settlement-run-row-${run.id}`}>
                <td>{run.label}</td>
                <td>{run.year}</td>
                <td>{t(RUN_STATUS_KEY[run.status], { locale: LOCALE })}</td>
                <td>
                  <button
                    type="button"
                    className="rh-btn"
                    onClick={() => void openRun(run)}
                    data-testid={`settlement-open-${run.id}`}
                  >
                    {t('webAdmin.spravaBytu.settlement.costsHeading', { locale: LOCALE })}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected !== null && (
        <>
          <FormSection title={t('webAdmin.spravaBytu.settlement.costsHeading', { locale: LOCALE })}>
            {advanceServices.map((entry) => {
              const draft = draftCosts[entry.key] ?? {
                amount: '',
                method: 'persons' as AllocationMethod,
              };
              return (
                <FieldRow
                  key={entry.key}
                  label={entry.label}
                  htmlFor={`settlement-cost-${entry.key}`}
                >
                  <div className="flex gap-2">
                    <CzechNumberInput
                      id={`settlement-cost-${entry.key}`}
                      className="rh-input w-40"
                      value={draft.amount}
                      onValueChange={(next) =>
                        setDraftCosts({
                          ...draftCosts,
                          [entry.key]: { ...draft, amount: next },
                        })
                      }
                      testId={`settlement-cost-${entry.key}`}
                    />
                    <select
                      className="rh-input"
                      value={draft.method}
                      aria-label={t('webAdmin.spravaBytu.settlement.costMethod', {
                        locale: LOCALE,
                      })}
                      onChange={(e) =>
                        setDraftCosts({
                          ...draftCosts,
                          [entry.key]: { ...draft, method: e.target.value as AllocationMethod },
                        })
                      }
                      data-testid={`settlement-method-${entry.key}`}
                    >
                      {(['persons', 'area', 'equal'] as const).map((method) => (
                        <option key={method} value={method}>
                          {t(METHOD_KEY[method], { locale: LOCALE })}
                        </option>
                      ))}
                    </select>
                  </div>
                </FieldRow>
              );
            })}
            <p className="text-xs text-gray-500">
              {t('webAdmin.spravaBytu.settlement.methodDirectHint', { locale: LOCALE })}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className="rh-btn"
                onClick={() => void saveCosts()}
                data-testid="settlement-save-costs"
              >
                {t('webAdmin.spravaBytu.settlement.saveCosts', { locale: LOCALE })}
              </button>
              <button
                type="button"
                className="rh-btn"
                disabled={costs.length === 0}
                onClick={() => void calculate()}
                data-testid="settlement-calculate"
              >
                {t('webAdmin.spravaBytu.settlement.calculate', { locale: LOCALE })}
              </button>
              <button
                type="button"
                className="rh-btn"
                disabled={selected.status !== 'calculated'}
                onClick={() => {
                  void closeSettlementRun(selected.id).then(() => reloadRuns());
                }}
                data-testid="settlement-close"
              >
                {t('webAdmin.spravaBytu.settlement.close', { locale: LOCALE })}
              </button>
            </div>
          </FormSection>

          {settlements.length > 0 && (
            <div className="rh-card p-4" data-testid="settlement-results">
              <p className="font-semibold">
                {t('webAdmin.spravaBytu.settlement.resultsHeading', { locale: LOCALE })}
              </p>
              <table className="rh-table mt-2">
                <thead>
                  <tr>
                    <th>{t('webAdmin.spravaBytu.settlement.colCosts', { locale: LOCALE })}</th>
                    <th>{t('webAdmin.spravaBytu.settlement.colAdvances', { locale: LOCALE })}</th>
                    <th>{t('webAdmin.spravaBytu.settlement.colBalance', { locale: LOCALE })}</th>
                    <th>
                      {t('webAdmin.spravaBytu.settlement.deadlineDelivery', { locale: LOCALE })}
                    </th>
                    <th>
                      {t('webAdmin.spravaBytu.settlement.deadlineSettlement', { locale: LOCALE })}
                    </th>
                    <th>{t('webAdmin.spravaBytu.settlement.colStatus', { locale: LOCALE })}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {settlements.map((settlement) => {
                    const overpaid = Number(settlement.balance) >= 0;
                    return (
                      <tr key={settlement.id} data-testid={`settlement-row-${settlement.id}`}>
                        <td>{money(settlement.totalCosts)}</td>
                        <td>{money(settlement.totalAdvances)}</td>
                        <td>
                          {t(
                            overpaid
                              ? 'webAdmin.spravaBytu.settlement.overpayment'
                              : 'webAdmin.spravaBytu.settlement.underpayment',
                            { locale: LOCALE },
                          )}{' '}
                          {money(
                            overpaid ? settlement.balance : String(-Number(settlement.balance)),
                          )}
                        </td>
                        <td>{settlement.deliveryDeadline}</td>
                        <td>{settlement.settlementDueDate ?? '—'}</td>
                        <td>{t(SETTLEMENT_STATUS_KEY[settlement.status], { locale: LOCALE })}</td>
                        <td>
                          {settlement.status === 'calculated' && (
                            <button
                              type="button"
                              className="rh-btn"
                              onClick={() => void act('deliver', settlement.id)}
                              data-testid={`settlement-deliver-${settlement.id}`}
                            >
                              {t('webAdmin.spravaBytu.settlement.deliver', { locale: LOCALE })}
                            </button>
                          )}
                          {settlement.status === 'delivered' && (
                            <button
                              type="button"
                              className="rh-btn"
                              onClick={() => void act('settle', settlement.id)}
                              data-testid={`settlement-settle-${settlement.id}`}
                            >
                              {t('webAdmin.spravaBytu.settlement.settle', { locale: LOCALE })}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <p className="mt-4 font-semibold">
                {t('webAdmin.spravaBytu.settlement.detailHeading', { locale: LOCALE })}
              </p>
              <table className="rh-table mt-2">
                <thead>
                  <tr>
                    <th>{t('webAdmin.spravaBytu.settlement.colService', { locale: LOCALE })}</th>
                    <th>{t('webAdmin.spravaBytu.settlement.costMethod', { locale: LOCALE })}</th>
                    <th>{t('webAdmin.spravaBytu.settlement.colBasis', { locale: LOCALE })}</th>
                    <th>{t('webAdmin.spravaBytu.settlement.colAllocated', { locale: LOCALE })}</th>
                    <th>
                      {t('webAdmin.spravaBytu.settlement.colServiceAdvances', { locale: LOCALE })}
                    </th>
                    <th>
                      {t('webAdmin.spravaBytu.settlement.colServiceBalance', { locale: LOCALE })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.flatMap((settlement) =>
                    settlement.lines.map((line) => (
                      <tr key={`${settlement.id}-${line.serviceKey}`}>
                        <td>{line.label}</td>
                        <td>{t(METHOD_KEY[line.method], { locale: LOCALE })}</td>
                        <td>{line.basis ?? '—'}</td>
                        <td>{money(line.allocatedCost)}</td>
                        <td>{money(line.advances)}</td>
                        <td>{money(line.balance)}</td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
